import { MoonfallCloud } from '../convex-client.js';
import { BootVeil } from './boot-veil.js';
import { LoadScheduler } from '../utils/load-scheduler.js';

interface WarmOptions { priority?: string; rank?: number; signal?: AbortSignal }
/**
 * Best-effort card-art warming. Visible, authoritative information gets high
 * priority; behind it the match downloads its art plan — the next draws, the
 * opponent's cards and the rest of the own deck, in the order the server
 * named them (game/utils/matchArtPlan.js). The opponent's art comes only from
 * the viewer's OWN view (`opponentArtAssets`); the opponent object's hand and
 * deck are never read.
 *
 * Every picture goes through the one app-wide LoadScheduler
 * (utils/load-scheduler.ts), so this module no longer decides on its own when
 * a download may start: a high-priority warm is foreground-gated (decoded and
 * handed to the raster sampler), a low-priority one is background-soon (bytes
 * only, never beside a foreground load, at most two at a time across every
 * surface), and the match's plan is a steady scope (three at a time, yielding
 * only to a picture the screen is loading).
 *
 * Preloading is an optimization, not a rendering dependency. Every wait is
 * bounded and every failure is swallowed so gameplay can continue normally.
 */


/* Declared and assigned in one step. `let AssetPreloader = {}` fixed its type
   as the empty object, so every member access through it — `warmCards`,
   `gateWarm`, `beginMatchWarm` — reported as a missing property in consumers,
   and the count only grew as callers were added. The IIFE's own return type is
   the accurate one. */
const AssetPreloader = (function () {
    const REVEAL_CAP_MS = 450;
    /* THE MATCH'S ART PLAN LOADS AS SOON AS THE FIRST SCREEN IS ON THE WIRE.
       The owner, 28 September 2026, after a match whose draws and enemy
       summons landed without their art ("the worst thing that was banned from
       happening ... I think main doesn't have this issue"): "in game, asap,
       all heavy assets should be loaded. but loading shouldn't wait for that
       since muligan and first rounds take a while", the hand first, then "the
       alternatives for mulligan", "the order of the deck", and the opponent's
       cards ("we could preload just the arts nothing else"). The one-at-a-time
       trickle it replaces waited for an idle slot, for quiet after every press
       and data read, and for every battle card still loading its art, and a
       foreground picture aborted it, so a draw a minute into the match could
       still find its card cold and pass the draw's 900 ms cap (cards.js
       renderPlayerHand). A 15-card deck is about 6 MB at the dev CDN's
       measured 409 KB mean (153-834 KB); both decks together about 12 MB, well
       inside a mulligan and a first turn on the owner's far 4G phone (8 Mbps).
       Three at a time keeps a 280 ms round trip from idling the link without
       splitting it so thin that the next draw waits behind the whole plan. */
    const PLAN_CONCURRENCY = 3;
    /* No plan where the player or the link cannot afford it: Save-Data (the
       player asked for fewer bytes), or a link the browser rates 2G (tens of
       kbps: the plan would take the whole match and halve the speed of the
       card actually drawn). Checked before every picture, so a link that
       degrades mid-match stops it. '3g' is NOT here: Chrome rates any link
       whose round trip is 272 ms or more '3g', whatever its bandwidth, so the
       far-away 4G phone the owner puts first would lose the warm draws it
       needs most. (Network Information API: Chromium only; elsewhere the link
       is unknown and the plan runs.) */
    const NO_TRICKLE_EFFECTIVE_TYPES = new Set(['slow-2g', '2g']);
    /* THE LOADING BAR LOADS ONLY THE FIRST SCREEN. The owner, 28 September
       2026: "if there is a loading bar, it should always load just the
       minimum". So the plan waits while the match's loading screen or the
       boot veil (a first visit) is up: they wait for textures, fonts and data
       the scheduler does not see, and a plan picture must never slow them.
       Each wait is capped, so a cover that never lifts cannot hold the plan
       back for good. */
    const COVER_YIELD_CAP_MS = 10000;
    /* The plan starts once the first screen's own pictures are ASKED for (their
       asset ids resolved): the steady scope then yields to them while they
       download. A resolver that hangs holds it no longer than this. */
    const VISIBLE_DECLARED_CAP_MS = 1500;
    /* A STELLAR CARD'S FILMS FOLLOW EVERY PICTURE, IN CHROMIUM ONLY ("the
       images or videos", the owner, 28 September 2026). Measured the same day
       (a cross-origin host sending Vary: Origin, like the art host): Chrome
       played a film that a bytes-only no-cors fetch() had downloaded with
       0 bytes from the network; Firefox and WebKit downloaded it again, so
       there a download ahead would only double the bytes. Chromium is the
       scheduler's own test for a shared fetch cache (`policy().fetchTransport`).
       The file is the one the card will play: WebM where the browser plays it
       (cards.js preferSupportedWebm; Chromium is never Safari). An opaque
       answer has no body to read, so a film counts as in flight until the
       browser records its download (Resource Timing), capped. */
    const FILM_WEBM_TYPE = 'video/webm; codecs="vp8, opus"';
    const FILM_DOWNLOAD_CAP_MS = 60000;
    /* A surface's low-priority warm (the Collection's next page, the
       Exchange's rows, the editor's next page) belongs to the lobby side: it
       may download on these screens, and entering the match closes it
       (LoadScheduler.enter). The deck downloads only in the match. */
    const SURFACE_SCREENS = ['boot', 'lobby', 'deck-selector', 'collection', 'store', 'foundry'] as const;
    const MATCH_SCREENS = ['match-loading', 'match'] as const;

    let visibleWarm = null;
    let idleWarmDone = Promise.resolve();
    let matchGeneration = 0;
    let planScope = null;
    let planKey = '';
    let surfaceScope = null;

    function isDirectUrl(value) {
        return typeof value === 'string' && /^(https?:|data:|blob:|\/)/.test(value);
    }

    function normalizePriority(priority) {
        return priority === 'low' ? 'low' : 'high';
    }

    function collectSources(sources) {
        const urls = [];
        const assetIds = [];
        for (const source of sources || []) {
            if (!source) continue;
            (isDirectUrl(source) ? urls : assetIds).push(source);
        }
        return {
            urls: [...new Set(urls)],
            assetIds: [...new Set(assetIds)],
        };
    }

    function cardSource(card) {
        return card?.imageUrl || card?.picture || card?.image || card?.id || null;
    }

    function portraitSource(entity) {
        if (!entity) return null;
        return entity.profilePictureUrl || entity.profilePicture || null;
    }

    async function resolveAssetUrls(assetIds) {
        if (!assetIds.length) return [];
        const media = MoonfallCloud?.media;
        if (!media?.getUrls) return [];
        try {
            const resolved = await media.getUrls(assetIds);
            return assetIds.map((id) => resolved?.[id]).filter(Boolean);
        } catch (err) {
            console.warn('[AssetPreloader] URL resolve failed', err);
            return [];
        }
    }

    function surfaceWarms() {
        if (!surfaceScope || surfaceScope.closed) {
            surfaceScope = LoadScheduler.scope('asset-preloader', { runsOn: SURFACE_SCREENS });
        }
        return surfaceScope;
    }

    /* High: ONE foreground-gated ask through the scheduler's one shared
       Image per URL (LoadScheduler.images), fixed to the URL's request mode
       before src is set, decoded and handed to the raster sampler
       (re-decoded when the sampler's bounded store has evicted it). One ask,
       so a picture of it already on the wire (a low warm) is raised before
       the fresh ones are sent, and in WebKit, where it cannot be raised, the
       fresh ones go level with it and it arrives first (load-scheduler.ts,
       PRIORITY ONCE SENT). Nothing cancels it on a timer: gateWarm caps the
       waiter, never the download. A supplied signal ends only an abandoned
       caller's demand, preserving other consumers. Low: background-soon,
       bytes only, waiting
       in `rank` order (lower first, 0 by default). The flow's next view
       passes a rank below 0 so it goes ahead of other surfaces' low warms
       already waiting: the Collection's next page sat behind eleven Moon
       Exchange row pictures declared a moment before it, and none of it had
       downloaded when Next was pressed (round 14, 26 September 2026, WebKit
       phone probe). */
    function warmImages(urls, { priority = 'high', rank = 0, signal }: WarmOptions = {}) {
        if (signal?.aborted || !urls.length) return [];
        if (normalizePriority(priority) === 'high') {
            return LoadScheduler.images(urls, 'foreground-gated', { signal }).map((warm) => warm.then(() => undefined));
        }
        const scope = signal
            ? LoadScheduler.scope('asset-preloader', { runsOn: SURFACE_SCREENS })
            : surfaceWarms();
        const abandon = () => scope.close();
        signal?.addEventListener('abort', abandon, { once: true });
        if (signal?.aborted) abandon();
        const warms = urls.map((url) => scope.soon([Number.isFinite(rank) && rank !== 0 ? { url, rank } : url]).then(() => undefined));
        if (signal) {
            void Promise.allSettled(warms).then(() => {
                signal.removeEventListener('abort', abandon);
                scope.close();
            });
        }
        return warms;
    }

    // Leaving the page ends its waiter too, even if asset URL resolution has
    // not answered. Resolution may finish, but cannot declare new pictures.
    function untilAbandoned(warm, signal?: AbortSignal) {
        if (!signal) return warm;
        if (signal.aborted) return Promise.resolve();
        return new Promise((resolve) => {
            const finish = () => {
                signal.removeEventListener('abort', finish);
                resolve(undefined);
            };
            signal.addEventListener('abort', finish, { once: true });
            Promise.resolve(warm).then(finish, finish);
            if (signal.aborted) finish();
        });
    }

    /* `settled`: every picture landed or failed. `asked`: every picture has
       been handed to the scheduler (asset ids resolved). */
    function warmSourcesTracked(sources, { priority = 'high', rank = 0, signal }: WarmOptions = {}) {
        if (signal?.aborted) return { settled: Promise.resolve(), asked: Promise.resolve() };
        const normalizedPriority = normalizePriority(priority);
        const { urls, assetIds } = collectSources(sources);
        const options = { priority: normalizedPriority, rank, signal };

        // Start direct URLs before the first asynchronous asset-resolution hop.
        const directWarms = warmImages(urls, options);
        const resolving = resolveAssetUrls(assetIds);
        const resolvedWarms = resolving.then((resolvedUrls) =>
            Promise.allSettled(warmImages(resolvedUrls, options)));

        return {
            settled: untilAbandoned(Promise.allSettled([...directWarms, resolvedWarms]).then(() => undefined), signal),
            asked: untilAbandoned(resolving.then(() => undefined, () => undefined), signal),
        };
    }

    function warmSources(sources, options = {}) {
        return warmSourcesTracked(sources, options).settled;
    }

    function warmCards(cards, options = {}) {
        const list = Array.isArray(cards) ? cards : [];
        return warmSources(list.map(cardSource), options);
    }

    function warmVisible(player, opponent) {
        const hand = Array.isArray(player?.hand) ? player.hand : [];
        const sources = hand.map(cardSource);
        sources.push(portraitSource(player), portraitSource(opponent));
        return warmSourcesTracked(sources, { priority: 'high' });
    }

    /* No trickle where the player or the link cannot afford it, checked by
       the scheduler before every picture (a link that degrades mid-match
       stops it at the next one). */
    function trickleAllowed(policy = LoadScheduler.policy()) {
        return !policy.saveData && !NO_TRICKLE_EFFECTIVE_TYPES.has(policy.effectiveType);
    }

    function matchCoverUp() {
        return document.getElementById('match-loading')?.getAttribute('aria-busy') === 'true';
    }

    /* The match's plan: a steady scope, only on the match's screens, waiting
       while the match's cover or the boot veil is up (COVER_YIELD_CAP_MS). */
    function openPlanScope() {
        const scope = LoadScheduler.scope('match-art', {
            runsOn: MATCH_SCREENS,
            maxInFlight: PLAN_CONCURRENCY,
            admit: trickleAllowed,
            steady: true,
        });
        scope.waitWhile('boot-veil', () => Boolean(BootVeil?.isUp?.()), COVER_YIELD_CAP_MS);
        scope.waitWhile('match-cover', matchCoverUp, COVER_YIELD_CAP_MS);
        return scope;
    }

    /* The server's order (game/utils/matchArtPlan.js): the next draws, the
       opponent's starting cards (cheapest first), the rest of the own deck.
       Read from the viewer's own object only; each source once. */
    function planSources(player) {
        const seen = new Set();
        const sources = [];
        for (const list of [player?.nextDrawArtAssets, player?.opponentArtAssets, player?.deckArtAssets]) {
            if (!Array.isArray(list)) continue;
            for (const source of list) {
                if (typeof source !== 'string' || !source || seen.has(source)) continue;
                seen.add(source);
                sources.push(source);
            }
        }
        return sources;
    }

    /* Asset ids resolve in one call, begun at once so it overlaps the first
       screen; the plan is then declared whole, in order. */
    function resolvePlan(sources) {
        const assetIds = sources.filter((source) => !isDirectUrl(source));
        if (!assetIds.length) return Promise.resolve(sources);
        const media = MoonfallCloud?.media;
        const lookup = media?.getUrls
            ? Promise.resolve(media.getUrls(assetIds)).catch((err) => {
                console.warn('[AssetPreloader] URL resolve failed', err);
                return null;
            })
            : Promise.resolve(null);
        return lookup.then((resolved) => sources
            .map((source) => (isDirectUrl(source) ? source : resolved?.[source] || null))
            .filter(Boolean));
    }

    function declarePlan(urls, scope) {
        const unique = [...new Set(urls)];
        return scope.soon(unique).then(() => undefined, () => undefined);
    }

    function playsWebm() {
        try {
            const verdict = String(document.createElement('video').canPlayType?.(FILM_WEBM_TYPE) || '').trim().toLowerCase();
            return verdict === 'probably' || verdict === 'maybe';
        } catch (_) {
            return false;
        }
    }

    function filmSources(player) {
        const pairs = Array.isArray(player?.stellarVideoAssets) ? player.stellarVideoAssets : [];
        const webm = playsWebm();
        const urls = pairs.map((pair) => (webm && pair?.webm ? pair.webm : pair?.mp4) || null)
            .filter((url) => typeof url === 'string' && /^https?:/.test(url));
        return [...new Set(urls)];
    }

    /* Resolves once the browser has recorded the download of `url` (or at the
       cap, or when the scope stops it). */
    function whenDownloaded(url, signal) {
        return new Promise((resolve) => {
            let observer = null;
            let timer = null;
            const done = () => {
                try { observer?.disconnect(); } catch (_) { /* gone */ }
                clearTimeout(timer);
                signal?.removeEventListener?.('abort', done);
                resolve(undefined);
            };
            if (typeof PerformanceObserver !== 'function') {
                done();
                return;
            }
            try {
                observer = new PerformanceObserver((list) => {
                    if (list.getEntriesByName(url).length) done();
                });
                observer.observe({ type: 'resource', buffered: true });
            } catch (_) {
                done();
                return;
            }
            timer = setTimeout(done, FILM_DOWNLOAD_CAP_MS);
            signal?.addEventListener?.('abort', done, { once: true });
        });
    }

    function declareFilms(urls, scope) {
        if (!urls.length || !LoadScheduler.policy().fetchTransport) return Promise.resolve(undefined);
        return Promise.all(urls.map((url) => scope.read('background-soon', (signal) => (
            fetch(url, { mode: 'no-cors', signal, priority: 'low' } as RequestInit)
                .then((response) => response.arrayBuffer().catch(() => null))
                .then(() => whenDownloaded(url, signal))
        )))).then(() => undefined, () => undefined);
    }

    function gateWarm(warm, timeoutMs = REVEAL_CAP_MS) {
        if (!warm || typeof warm.then !== 'function') return Promise.resolve('no-warm');
        return new Promise((resolve) => {
            let settled = false;
            let timer = null;
            const finish = (result) => {
                if (settled) return;
                settled = true;
                if (timer !== null) clearTimeout(timer);
                resolve(result);
            };
            timer = setTimeout(() => finish('timeout'), timeoutMs);
            Promise.resolve(warm).then(
                () => finish('warm'),
                () => finish('warm'),
            );
        });
    }

    /**
     * Starts a new snapshot generation. The first screen (the local starting
     * hand and both public portraits) is warmed at high priority; the match's
     * art plan follows once those pictures are asked for (PLAN_CONCURRENCY
     * says why and how). The start's own view already names the whole plan,
     * so no later snapshot has anything to add to it.
     */
    function beginMatchWarm(snapshot) {
        const player = snapshot?.player;
        const opponent = snapshot?.opponent;
        const sources = planSources(player);
        const key = sources.join('\n');
        const visible = warmVisible(player, opponent);
        const tier0 = visible.settled.catch((err) => {
            console.warn('[AssetPreloader] visible warm failed', err);
        });
        visibleWarm = tier0;
        /* The same match started again (its mulligan shown again by a later
           snapshot, a reconnect) keeps the plan it is running: closing it
           would drop the pictures on the wire and ask for them again. */
        if (key && key === planKey && planScope && !planScope.closed) return tier0;

        const generation = ++matchGeneration;
        // The previous match's plan is no longer anyone's next screen.
        planScope?.close();
        const scope = openPlanScope();
        planScope = scope;
        planKey = key;
        const plan = resolvePlan(sources);
        idleWarmDone = Promise.all([gateWarm(visible.asked, VISIBLE_DECLARED_CAP_MS), plan]).then(([, urls]) => {
            if (generation !== matchGeneration || scope.closed || !trickleAllowed()) return undefined;
            // Declared together, so the films queue behind every picture.
            return Promise.all([declarePlan(urls, scope), declareFilms(filmSources(player), scope)]);
        }).then(() => undefined, () => undefined);
        return tier0;
    }

    /** The player left the match: its plan stops (lobby.js tearDownInGameUI). */
    function endMatchWarm() {
        matchGeneration += 1;
        planScope?.close();
        planScope = null;
        planKey = '';
    }

    function gateReveal(timeoutMs = REVEAL_CAP_MS) {
        return visibleWarm ? gateWarm(visibleWarm, timeoutMs) : Promise.resolve('no-warm');
    }

    function whenIdleWarmDone() {
        return idleWarmDone;
    }

    return {
        beginMatchWarm,
        endMatchWarm,
        gateReveal,
        gateWarm,
        warmCards,
        // Pictures named before their match exists (the guest tutorial's
        // first screen, guest-session.js).
        warmSources,
        whenIdleWarmDone,
    };
})();

export { AssetPreloader };
